
import React, { useState, useEffect } from 'react';
import { X, Shield } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AppUser } from '../../types';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';

interface UserFormModalProps {
  user?: AppUser | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const UserFormModal: React.FC<UserFormModalProps> = ({ user, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({ full_name: '', email: '', role: 'viewer' as any });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) setFormData({ full_name: user.full_name || '', email: user.email || '', role: user.role });
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (user) {
        const { error } = await supabase.from('users').update(formData).eq('id', user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('users').insert([{ ...formData, email: formData.email.toLowerCase().trim() }]);
        if (error) throw error;
      }
      onSuccess();
      onClose();
    } catch (err: any) { alert(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-100 animate-in zoom-in-95 duration-200">
        <div className="p-8 border-b flex items-center justify-between">
          <h3 className="text-2xl font-black uppercase text-slate-900 flex items-center gap-3">
            <Shield size={24} className="text-[#FFDB00]" />
            {user ? 'Modify Profile' : 'Define Identity'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={24} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-10 space-y-6">
          <Input label="Full Name" required value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} />
          <Input label="Email Address" required type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} disabled={!!user} />
          
          <div className="space-y-2">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">System Role</label>
            <select className="w-full px-5 py-3 bg-white border border-slate-200 rounded-xl font-bold outline-none focus:ring-4 focus:ring-slate-100" value={formData.role} onChange={e => setFormData({...formData, role: e.target.value as any})}>
              <option value="system_admin">System Admin</option>
              <option value="org_manager">Org Manager</option>
              <option value="staff">Staff</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>

          <div className="pt-6 flex gap-4">
            <Button type="button" variant="ghost" onClick={onClose} className="flex-1">Discard</Button>
            <Button type="submit" isLoading={loading} variant="primary" className="flex-1 py-4">
              {user ? 'Update Record' : 'Commit Identity'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
